import React, { useEffect, useState } from "react";
import { RiCloseCircleFill } from "react-icons/ri";
import { useSelector } from "react-redux";
import { getPaymentSettings, getUser } from "store/selector/auth.selector";
import { classNames, getExpireDate } from "Utils/utils";
import {
  STRIPE_CHECK_SUBSCRIPTION_STATUS,
  checkSubscriptionStatus,
  isOverParticipantLimit,
} from "helpers/RolesPermission";
import { useNavigate } from "react-router";

const CtaLink = ({ children, onClick, className = "mr-1 text-indigo-700 underline cursor-pointer" }) => (
  <span
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => e.key === "Enter" && onClick?.()}
    className={className}
  >
    {children}
  </span>
);

const VertexBanner = ({ setIsTrailChoosePlanBtnClick }) => {
  const [isOpen, setIsOpen] = useState(false);

  const currentUser = useSelector(getUser);
  const paymentSetting = useSelector(getPaymentSettings);
  const subscriptionStatus = checkSubscriptionStatus(currentUser);
  const overLimit = isOverParticipantLimit({ payment_setting: paymentSetting });
  const limitStatus = paymentSetting?.participant_limit_status;
  const daysUntilReadOnly = limitStatus?.days_until_read_only;

  const navigate = useNavigate();

  useEffect(() => {
    if (overLimit) {
      setIsOpen(true);
    } else if (subscriptionStatus === STRIPE_CHECK_SUBSCRIPTION_STATUS.SUBSCRIBED) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, [subscriptionStatus, overLimit]);

  const navigateToPlan = () => {
    setIsTrailChoosePlanBtnClick((prev) => prev + 1);
    navigate("/subscription/plans");
  };

  const navigateToSubscription = () => navigate("/subscription");

  return (
    <div
      className={classNames(
        "border-t-0 border-b-2 border border-1 flex items-center gap-x-6 bg-amber-300 px-6 py-2.5 sm:px-3.5 sm:before:flex-1",
        isOpen === false && "hidden"
      )}
    >
      <p className='text-sm leading-6 text-black w-[60%] text-center'>
        <span>
          {limitStatus?.read_only_due_to_participant_limit ? (
            <>
              Your account is in read-only mode because you have exceeded your
              participant limit. Please upgrade or contact us on 1300 866 959.
              <br />
              <CtaLink onClick={navigateToPlan}>Click here</CtaLink>
              to select a plan and restore full access.
            </>
          ) : limitStatus?.over_limit ? (
            <>
              You have exceeded your active participant limit. Please upgrade, or
              contact us on 1300 866 959 for any question related to your plan.
              {daysUntilReadOnly != null && daysUntilReadOnly > 0 && (
                <> Your account will become read-only in {daysUntilReadOnly} day{daysUntilReadOnly !== 1 ? "s" : ""}.</>
              )}
              <br />
              <CtaLink onClick={navigateToPlan}>Click here</CtaLink>
              to upgrade your plan.
            </>
          ) : subscriptionStatus === STRIPE_CHECK_SUBSCRIPTION_STATUS.TRAIL_EXPIRED ? (
            <>
              Attention! Your trial period for Vertex360 has ended. To continue
              accessing our services, please choose a suitable plan now.
              <br />
              <CtaLink onClick={navigateToPlan}>Click here</CtaLink>
              to select your plan and unlock all features.
            </>
          ) : subscriptionStatus === STRIPE_CHECK_SUBSCRIPTION_STATUS.SUBSCRIPTION_UNPAID ? (
            <>
              Your account is currently in 'Read Only' mode. Update payment card
              details for uninterrupted service.
              <br />
              <CtaLink onClick={navigateToSubscription}>Click here</CtaLink>
              to update payment card details.
            </>
          ) : subscriptionStatus === STRIPE_CHECK_SUBSCRIPTION_STATUS.RETRY_PAYMENT_FAILED ? (
            <>
              {`Enjoy a one month grace period: Your access to Vertex360 is until ${getExpireDate(
                paymentSetting?.next_payment_attempt
              )}. After this date, your account will have limited access
              until payment is made. To avoid any disruption in service, please
              ensure payment is processed before the grace period ends. Thank
              you for your cooperation. `}
              <CtaLink onClick={navigateToSubscription} className="ml-1 text-indigo-700 underline cursor-pointer">Click here</CtaLink>
            </>
          ) : subscriptionStatus === STRIPE_CHECK_SUBSCRIPTION_STATUS.PAYMENTFAILED ? (
            <>
              Attention: Your Vertex360 subscription is presently inactive due
              to payment issues. To regain access to all features, please update
              your payment information.
            </>
          ) : subscriptionStatus === STRIPE_CHECK_SUBSCRIPTION_STATUS.PAYMENT_FAILED_TRAIL_EXISTS ? (
            <>
              <span className='font-bold'>Attention:</span> Your Vertex360
              subscription is presently inactive due to payment issues. To
              regain access to all features, please update your payment
              information.
              <br />
              <span className='underline'>
                {`You can still use a trial account that will expire on ${getExpireDate(
                  paymentSetting?.trial_end_date
                )}`}
              </span>
            </>
          ) : (
            subscriptionStatus === STRIPE_CHECK_SUBSCRIPTION_STATUS.TRAILING && (
              <>
                {`Enjoy exploring Vertex360's features during your trial period, valid until ${getExpireDate(
                  paymentSetting?.trial_end_date
                )}.`}
                {` Upgrade your subscription plan for even greater benefits and functionality – `}
                <CtaLink onClick={navigateToPlan} className="ml-1 text-indigo-700 underline cursor-pointer">
                  subscribe now!
                </CtaLink>
              </>
            )
          )}
        </span>
      </p>

      <div className='flex flex-1 justify-end'>
        {subscriptionStatus === false && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className='-m-3 p-3 focus-visible:outline-offset-[-4px]'
          >
            <span className='sr-only'>Dismiss</span>
            <RiCloseCircleFill
              className='h-5 w-5 text-black'
              aria-hidden='true'
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default VertexBanner;
